/**
 * Mimu — show the real one-bag price beside a struck-through list price on each
 * flavour row of the Appstle mix-and-match widget, and tag the widget heading.
 *
 * v7 fixes a drift bug: v1-v6 struck a HARDCODED 20% off the row's list price
 * (64.99 -> 51.99) instead of reading Appstle's own "1 bag" tier price. Every
 * time the tier price was changed in Appstle admin (e.g. to 56.99), the row kept
 * showing the stale 51.99 forever, silently disagreeing with the tier ladder
 * above it. v7 reads the live "1 bag" tier tab instead, so it always matches
 * whatever the tier is currently configured to.
 *
 * v6 also breaks each flavour's description onto two lines: the taste note, then
 * the weight sentence. Split at the FIRST weight mention rather than at a fixed
 * phrase, so it keeps working if the copy is reworded — "Toasty coconut 210g…"
 * and "Smooth with a hint of vanilla. 210g…" both break in the right place.
 *
 * v5 also holds the widget's add-to-cart button in an empty state until at least
 * one bag is staged: it sets data-mimu-empty, and the CSS in mimu-widget-price.liquid
 * greys the bar and swaps its label. The label swap is done in CSS rather than by
 * rewriting the button's text, so Appstle's own wording is never destroyed and
 * comes straight back the moment a bag is added.
 *
 * v4 fixes a real bug in v2/v3: the struck price climbed as quantity increased.
 * Those versions re-derived the list price from whatever money text the row was
 * currently showing. When a shopper adds a second bag Appstle re-renders the row
 * with the LINE TOTAL, so $129.98 was read as a fresh list price and struck
 * instead of $64.99. Dropping back to zero could also leave the row un-rewritten,
 * because a stale marked node elsewhere in the row made the whole row look done.
 *
 * The fix is to stop deriving anything from the live DOM after the first read.
 * Each row's unit price is captured once, keyed by its position in the widget, and
 * every later re-render is painted from that stored figure. Quantity is divided
 * out on capture, and the smallest value ever seen wins, so a row that is somehow
 * first seen at a multiple still settles on the true unit price.
 *
 * Row markup is matched by climbing from '.ab-mix-and-match-add-button' — a proven
 * anchor, since mimu-launch-extras.liquid draws the "+" in "Add +" onto it. The
 * per-row price has no class name discoverable from the server: the widget renders
 * client-side and Appstle's own stylesheet covers only their other widget types.
 */
(function () {
  var ANCHOR = '.ab-mix-and-match-add-button';
  var QTY = '.ab-mix-and-match-quantity-value';
  var ATC = '.ab-mix-and-match-add-to-cart-button';
  var THUMBS = '.ab-mix-and-match-summary-thumbnails';
  var NO_SELECTION = '.ab-mix-and-match-no-selection';

  // The "1 bag" tier tab in Appstle's own tier ladder — matched by its visible
  // qty text ("1 bag"), not by tab position, in case Appstle ever reorders tabs.
  // Its price line reads like "$56.99/ea " (tier 1) or "$52.99/ea + free shipping"
  // (higher tiers), so the money pattern below is unanchored, unlike MONEY.
  var TIER_TAB = '.ab-mix-and-match-tier-tab';
  var TIER_QTY = '.ab-mix-and-match-tier-tab-qty-value';
  var TIER_PRICE = '.ab-mix-and-match-tier-tab-discount';
  var ONE_BAG = /^\s*1\s*bag/i;
  var TIER_MONEY = /([$£€¥₹])\s*([\d.,]+)/;

  var MAX_CLIMB = 6;

  var SKIP = [
    '.ab-mix-and-match-summary-prices',
    '.ab-mix-and-match-tier-tabs',
    '.ab-mix-and-match-tier-tab'
  ].join(',');

  var MONEY = /^\s*([$£€¥₹])\s*([\d.,]+)\s*$/;

  // First weight in the sentence — "210g", "210 g" — starts the second line.
  var WEIGHT = /\d+\s?g\b/;

  // Unit price per row, by row index. Written once, then only ever lowered.
  var units = [];
  var symbols = [];

  var scheduled = false;

  function money(symbol, value, sample) {
    var fixed = value.toFixed(2);
    if (sample && sample.indexOf(',') !== -1) {
      var parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      fixed = parts.join('.');
    }
    return symbol + fixed;
  }

  /** The row's price node: one we already rewrote, or the deepest money leaf. */
  function priceElement(scope) {
    var done = scope.querySelector('[data-mimu-wp]');
    if (done) return done;

    var all = scope.querySelectorAll('*');
    var best = null;
    var bestDepth = -1;

    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.closest(SKIP)) continue;
      if (!MONEY.test(el.textContent || '')) continue;

      var depth = 0;
      var node = el;
      while (node && node !== scope) { depth++; node = node.parentElement; }
      if (depth > bestDepth) { best = el; bestDepth = depth; }
    }
    return best;
  }

  /** How many of this flavour are currently staged, 0 when not readable. */
  function quantity(scope) {
    var el = scope.querySelector(QTY);
    if (!el) return 0;
    var m = (el.textContent || '').match(/\d+/);
    return m ? parseInt(m[0], 10) : 0;
  }

  /** Appstle's live "1 bag" tier price, or null when it cannot be read. */
  function oneBagPrice() {
    var qtyEls = document.querySelectorAll(TIER_QTY);
    for (var i = 0; i < qtyEls.length; i++) {
      if (!ONE_BAG.test(qtyEls[i].textContent || '')) continue;

      var tab = qtyEls[i].closest(TIER_TAB);
      var priceEl = tab && tab.querySelector(TIER_PRICE);
      if (!priceEl) return null;

      var m = TIER_MONEY.exec(priceEl.textContent || '');
      if (!m) return null;

      var value = parseFloat(m[2].replace(/,/g, ''));
      return value > 0 ? value : null;
    }
    return null;
  }

  function apply() {
    var buttons = document.querySelectorAll(ANCHOR);

    Array.prototype.forEach.call(buttons, function (button, index) {
      var row = button;

      for (var i = 0; i < MAX_CLIMB && row.parentElement; i++) {
        row = row.parentElement;

        splitDescription(row);

        var el = priceElement(row);
        if (!el) continue;

        var symbol = symbols[index];
        var unit = units[index];

        if (!el.hasAttribute('data-mimu-wp')) {
          // Fresh render. Read it, but divide the quantity back out first — the
          // number on screen is a line total once more than one bag is staged.
          var match = MONEY.exec(el.textContent || '');
          if (!match) return;

          var shown = parseFloat(match[2].replace(/,/g, ''));
          if (!(shown > 0)) return;

          var qty = quantity(row);
          var candidate = qty > 1 ? shown / qty : shown;
          candidate = Math.round(candidate * 100) / 100;

          symbol = symbols[index] = match[1];
          unit = units[index] = unit === undefined ? candidate : Math.min(unit, candidate);
        }

        if (!(unit > 0) || !symbol) return;

        // Read live every pass rather than caching, so a price change in Appstle
        // admin is reflected without a page reload. Unreadable: leave the row as
        // is instead of guessing — a stale hardcoded percentage is the bug this
        // version fixes.
        var payable = oneBagPrice();
        if (!(payable > 0) || payable >= unit) return;

        var html =
          '<s class="mimu-wp mimu-wp__was">' + money(symbol, unit) + '</s>' +
          '<span class="mimu-wp mimu-wp__now">' + money(symbol, payable) + '</span>';

        // Write only on a real change, or the observer feeds on its own output.
        if (el.innerHTML !== html) {
          el.setAttribute('data-mimu-wp', '1');
          el.innerHTML = html;
        }
        return;
      }
    });
  }

  // ---- flavour descriptions -------------------------------------------------
  function escapeHtml(value) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /**
   * Break a row's description into "taste note" + "weight sentence".
   *
   * Leaf elements only, and anything already handled is skipped — once rewritten
   * the node holds a <br>, so it has an element child and fails the leaf test on
   * the next pass. That is what stops the observer feeding on its own output.
   */
  function splitDescription(row) {
    var all = row.querySelectorAll('*');

    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.children.length) continue;
      if (el.hasAttribute('data-mimu-desc')) return;
      if (el.closest(SKIP)) continue;

      var text = (el.textContent || '').trim();
      if (text.length < 20) continue;   // titles, prices, button labels
      if (MONEY.test(text)) continue;

      var match = WEIGHT.exec(text);
      if (!match || match.index === 0) continue;

      var head = text.slice(0, match.index).trim();
      var tail = text.slice(match.index).trim();
      if (!head || !tail) continue;

      el.setAttribute('data-mimu-desc', '1');
      el.innerHTML = escapeHtml(head) + '<br>' + escapeHtml(tail);
      return;
    }
  }

  // ---- add-to-cart button ---------------------------------------------------
  /**
   * Total bags staged in the widget, or null when it cannot be read.
   *
   * Same approach the sticky bar uses: trust the per-product steppers, and fall
   * back to Appstle's explicit "no product selected" marker. Never infer a count
   * from how many children the thumbnail strip has — that marker is itself a
   * child and would read as one bag.
   */
  function stagedCount() {
    var qtyEls = document.querySelectorAll(QTY);
    if (qtyEls.length) {
      var total = 0;
      var readAny = false;
      Array.prototype.forEach.call(qtyEls, function (el) {
        var m = (el.textContent || '').match(/\d+/);
        if (m) { total += parseInt(m[0], 10); readAny = true; }
      });
      if (readAny) return total;
    }

    var box = document.querySelector(THUMBS);
    if (!box) return null;
    if (box.querySelector(NO_SELECTION)) return 0;
    if (box.querySelectorAll('img').length) return 1;
    return null;
  }

  function syncAddToCart() {
    var button = document.querySelector(ATC);
    if (!button) return;

    var count = stagedCount();
    // Unreadable: leave whatever state it is in rather than guessing, so a slow
    // render can never grey out a button that is genuinely ready.
    if (count === null) return;

    if (count > 0) button.removeAttribute('data-mimu-empty');
    else if (!button.hasAttribute('data-mimu-empty')) button.setAttribute('data-mimu-empty', '1');
  }

  // ---- widget heading -------------------------------------------------------
  var TITLE = /^\s*build your bundle\.?\s*$/i;

  function tagTitle() {
    if (document.querySelector('[data-mimu-bundle-title]')) return;

    var all = document.body.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.children.length) continue;
      var tag = el.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'TEMPLATE') continue;
      if (!TITLE.test(el.textContent || '')) continue;
      el.setAttribute('data-mimu-bundle-title', '1');
      return;
    }
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      apply();
      syncAddToCart();
      tagTitle();
    });
  }

  window.mimuWidgetPrice = {
    apply: apply,
    anchors: function () { return document.querySelectorAll(ANCHOR).length; },
    rewritten: function () { return document.querySelectorAll('[data-mimu-wp]').length; },
    staged: stagedCount,
    descriptions: function () { return document.querySelectorAll('[data-mimu-desc]').length; },
    title: function () { return document.querySelectorAll('[data-mimu-bundle-title]').length; },
    units: function () { return units.slice(); }
  };

  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('load', schedule);
  setInterval(schedule, 800);
  schedule();
})();
